/**
 * MentionTextarea — F4 @mention helper for note inputs.
 *
 * When the user types "@" the picker shows the case's assigned users
 * (Processor + Sales Rep). Selecting one inserts "@First " at the cursor.
 * Plain Textarea otherwise.
 */
import { useEffect, useRef, useState } from "react";
import { Textarea } from "@/components/ui/textarea";

export default function MentionTextarea({
    value,
    onChange,
    onMentionPick,    // F4.1: (user) => void — called when user clicks/keys a picker entry
    candidates = [], // [{id, name, email, label}]
    placeholder,
    rows = 3,
    testId,
}) {
    const taRef = useRef(null);
    const [showPicker, setShowPicker] = useState(false);
    const [pickerIndex, setPickerIndex] = useState(0);
    const [anchorPos, setAnchorPos] = useState({ left: 0, top: 0 });

    useEffect(() => {
        if (!showPicker) setPickerIndex(0);
    }, [showPicker]);

    const close = () => setShowPicker(false);

    const detectTrigger = (el) => {
        if (!el) return;
        const pos = el.selectionStart;
        const text = el.value.slice(0, pos);
        // Trigger when "@" was the last char OR the cursor is on a partial
        // @token with no whitespace between cursor and the @.
        const lastAt = text.lastIndexOf("@");
        if (lastAt === -1) return close();
        const between = text.slice(lastAt + 1);
        if (/\s/.test(between)) return close();
        if (between.length > 30) return close();
        // Show picker. We position it just below the textarea (simple).
        const rect = el.getBoundingClientRect();
        setAnchorPos({ left: 0, top: rect.height });
        setShowPicker(candidates.length > 0);
    };

    const filtered = (() => {
        const el = taRef.current;
        if (!el || !showPicker) return candidates;
        const text = el.value.slice(0, el.selectionStart);
        const lastAt = text.lastIndexOf("@");
        if (lastAt === -1) return candidates;
        const q = text.slice(lastAt + 1).toLowerCase();
        if (!q) return candidates;
        return candidates.filter(c => (c.name || "").toLowerCase().includes(q));
    })();

    const insertMention = (user) => {
        const el = taRef.current;
        if (!el) return;
        const pos = el.selectionStart;
        const before = el.value.slice(0, pos);
        const after = el.value.slice(pos);
        const lastAt = before.lastIndexOf("@");
        const firstName = (user.name || "").split(" ")[0];
        const newBefore = before.slice(0, lastAt) + `@${firstName} `;
        const next = newBefore + after;
        onChange(next);
        onMentionPick?.(user);
        setShowPicker(false);
        requestAnimationFrame(() => {
            if (taRef.current) {
                taRef.current.focus();
                const newPos = newBefore.length;
                taRef.current.setSelectionRange(newPos, newPos);
            }
        });
    };

    const onKeyDown = (e) => {
        if (!showPicker || filtered.length === 0) return;
        if (e.key === "ArrowDown") { e.preventDefault(); setPickerIndex(i => Math.min(filtered.length - 1, i + 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setPickerIndex(i => Math.max(0, i - 1)); }
        else if (e.key === "Enter" || e.key === "Tab") {
            e.preventDefault();
            insertMention(filtered[pickerIndex]);
        } else if (e.key === "Escape") {
            setShowPicker(false);
        }
    };

    return (
        <div className="relative">
            <Textarea
                ref={taRef}
                value={value}
                onChange={(e) => { onChange(e.target.value); detectTrigger(e.target); }}
                onClick={(e) => detectTrigger(e.target)}
                onKeyUp={(e) => { if (!["Enter", "ArrowDown", "ArrowUp", "Escape", "Tab"].includes(e.key)) detectTrigger(e.target); }}
                onKeyDown={onKeyDown}
                onBlur={() => setTimeout(close, 150)}
                rows={rows}
                placeholder={placeholder}
                data-testid={testId}
            />
            {showPicker && filtered.length > 0 && (
                <div
                    className="absolute z-30 bg-white border border-border rounded-md shadow-lg min-w-[220px] overflow-hidden"
                    style={{ left: anchorPos.left, top: anchorPos.top + 4 }}
                    data-testid={`${testId}-mention-picker`}
                >
                    {filtered.map((c, i) => (
                        <button
                            key={c.id}
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); insertMention(c); }}
                            className={`block w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 ${i === pickerIndex ? "bg-gray-100" : ""}`}
                            data-testid={`${testId}-mention-pick-${c.id}`}
                        >
                            <div className="font-medium">{c.name}</div>
                            <div className="text-xs text-muted-foreground">{c.label || c.email}</div>
                        </button>
                    ))}
                </div>
            )}
            {showPicker && filtered.length === 0 && (
                <div
                    className="absolute z-30 bg-white border border-border rounded-md shadow-lg min-w-[220px] px-3 py-2 text-xs text-muted-foreground"
                    style={{ left: anchorPos.left, top: anchorPos.top + 4 }}
                    data-testid={`${testId}-mention-empty`}
                >
                    No assigned users.
                </div>
            )}
        </div>
    );
}
